import { ContactDialog } from "@/components/contacts/ContactDialog";
import { SalesOrderSheet } from "@/components/operations";
import {
  buildInventoryWorkspacePath,
  formatStatusLabel,
} from "@/components/operations/operations-shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ClientDataSchema, type Client } from "@/core";
import {
  useAllClients,
  useClientById,
  useCreateClient,
  useDeleteClient,
  useSalesOrdersByClientId,
  useUpdateClient,
} from "@/hooks";
import { ContactFormValues } from "@/hooks/forms/entities/use-contact-form";
import { useUserStore } from "@/stores/user";
import {
  formatTimestamp,
  getClientDisplayName,
  getClientSearchKeywords,
  removeUndefinedValues,
} from "@/utils";
import { AlertCircle, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const OPTIONAL_CLIENT_FIELDS = [
  "secondaryPhone",
  "secondaryEmail",
  "companyName",
] as const;

function getMissingClientRequirements({ client }: { client: Client }) {
  return [
    !client.email?.trim() ? "Missing email" : null,
    !client.phone?.trim() ? "Missing phone" : null,
    !client.type?.trim() ? "Missing type" : null,
  ].filter((value): value is string => Boolean(value));
}

export default function ClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedClientId = searchParams.get("clientId") || "";
  const selectedSoId = searchParams.get("soId") || "";
  const [searchTerm, setSearchTerm] = useState("");
  const [idSearchTerm, setIdSearchTerm] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const { hasFullAccess, hasClientVendorWriteAccess } = useUserStore();

  const isIdSearchActive = idSearchTerm.trim().length > 0;

  const clientsQuery = useAllClients();
  const clientQuery = useClientById(selectedClientId);
  const clientByIdSearchQuery = useClientById(isIdSearchActive ? idSearchTerm.trim() : "");
  const salesOrdersQuery = useSalesOrdersByClientId(selectedClientId);
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();
  const deleteClientMutation = useDeleteClient();

  const clients = useMemo(
    () => clientsQuery.data?.pages.flat() ?? [],
    [clientsQuery.data],
  );
  const filteredClients = useMemo(() => {
    if (isIdSearchActive) {
      return clientByIdSearchQuery.data ? [clientByIdSearchQuery.data] : [];
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return clients;
    }

    return clients.filter((client) =>
      getClientSearchKeywords({ client }).some((value) =>
        value.toLowerCase().includes(normalizedSearch),
      ),
    );
  }, [clients, searchTerm, isIdSearchActive, clientByIdSearchQuery.data]);
  const salesOrders = salesOrdersQuery.data ?? [];
  const selectedClient =
    clients.find((client) => client.id === selectedClientId) || clientQuery.data;

  const clientLabel = selectedClient
    ? getClientDisplayName({ client: selectedClient })
    : "Select a client";

  const normalizeClientFormValues = ({ data }: { data: ContactFormValues }) => ({
    ...data,
    secondaryPhone: data.secondaryPhone?.trim() || undefined,
    secondaryEmail: data.secondaryEmail?.trim() || undefined,
    companyName: data.companyName?.trim() || undefined,
    type: data.type || undefined,
  });

  const handleCreateClient = async (data: ContactFormValues) => {
    const normalizedData = normalizeClientFormValues({ data });
    const parsedData = ClientDataSchema.parse(normalizedData);
    const cleanData = removeUndefinedValues(parsedData);
    const clientId = await createClientMutation.mutateAsync({
      data: cleanData,
    });

    setIsCreateDialogOpen(false);
    setSearchParams({ clientId });
  };

  const handleUpdateClient = async (data: ContactFormValues) => {
    if (!editingClient) {
      return;
    }

    const normalizedData = normalizeClientFormValues({ data });
    const parsedData = ClientDataSchema.parse(normalizedData);
    const cleanData = removeUndefinedValues(parsedData);
    const updateData = { ...cleanData };

    for (const field of OPTIONAL_CLIENT_FIELDS) {
      if (editingClient[field] && normalizedData[field] === undefined) {
        updateData[field] = "";
      }
    }

    await updateClientMutation.mutateAsync({
      id: editingClient.id,
      data: updateData,
    });
    setEditingClient(null);
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) {
      return;
    }

    await deleteClientMutation.mutateAsync({
      id: clientToDelete.id,
    });

    if (selectedClientId === clientToDelete.id) {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.delete("clientId");
        next.delete("soId");
        return next;
      });
    }

    setClientToDelete(null);
  };

  return (
    <div className="container mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Clients</CardTitle>
              <CardDescription>
                Manage clients and inspect the sales orders linked to them.
              </CardDescription>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative min-w-[16rem]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    if (event.target.value) setIdSearchTerm("");
                  }}
                  disabled={isIdSearchActive}
                />
              </div>
              <div className="relative min-w-[16rem]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by client ID..."
                  value={idSearchTerm}
                  onChange={(event) => {
                    setIdSearchTerm(event.target.value);
                    if (event.target.value) setSearchTerm("");
                  }}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => clientsQuery.refetch()}
                disabled={clientsQuery.isFetching}
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${clientsQuery.isFetching ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
              {hasClientVendorWriteAccess() ? (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add client
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="w-[140px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isIdSearchActive && clientByIdSearchQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    Looking up client...
                  </TableCell>
                </TableRow>
              ) : !isIdSearchActive &&
                (clientsQuery.isLoading || clientsQuery.hasNextPage) ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    Loading clients...
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    {isIdSearchActive
                      ? `No client found with ID "${idSearchTerm.trim()}".`
                      : "No clients found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => {
                  const displayName = getClientDisplayName({ client });
                  const missingRequirements = getMissingClientRequirements({ client });

                  return (
                    <TableRow
                      key={client.id}
                      className="cursor-pointer"
                      onClick={() => setSearchParams({ clientId: client.id })}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="space-y-1">
                            <div>{displayName}</div>
                            {client.type ? (
                              <div className="text-xs capitalize text-muted-foreground">
                                {client.type}
                              </div>
                            ) : null}
                          </div>
                          {missingRequirements.length > 0 ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <AlertCircle className="h-4 w-4 text-amber-500" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" sideOffset={6} className="text-black">
                                {missingRequirements.join(" and ")}
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div>{client.email}</div>
                          {client.secondaryEmail ? (
                            <div className="text-xs text-muted-foreground">
                              {client.secondaryEmail}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div>{client.phone}</div>
                          {client.secondaryPhone ? (
                            <div className="text-xs text-muted-foreground">
                              {client.secondaryPhone}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className="flex items-center gap-1"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {hasClientVendorWriteAccess() ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              onClick={() => setEditingClient(client)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {hasFullAccess() ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8 text-destructive"
                              onClick={() => setClientToDelete(client)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {!hasClientVendorWriteAccess() ? (
                            <span className="text-sm text-muted-foreground">View only</span>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{clientLabel}</CardTitle>
          <CardDescription>
            Sales orders associated with the selected client.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!selectedClientId ? (
            <div className="text-sm text-muted-foreground">
              Select a client to view linked sales orders.
            </div>
          ) : salesOrdersQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">
              Loading sales orders...
            </div>
          ) : salesOrders.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No sales orders are linked to this client yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Miles Sold</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesOrders.map((salesOrder) => (
                  <TableRow
                    key={salesOrder.id}
                    className="cursor-pointer"
                    onClick={() =>
                      setSearchParams({
                        clientId: selectedClientId,
                        soId: salesOrder.id,
                      })}
                  >
                    <TableCell>{salesOrder.productName}</TableCell>
                    <TableCell>{formatStatusLabel(salesOrder.status)}</TableCell>
                    <TableCell>{formatStatusLabel(salesOrder.salesChannel)}</TableCell>
                    <TableCell>{salesOrder.totalMilesSold.toLocaleString()}</TableCell>
                    <TableCell>
                      {formatTimestamp(salesOrder.createdAt, "yyyy-MM-dd HH:mm") || "-"}
                    </TableCell>
                    <TableCell>
                      <div
                        className="flex items-center gap-2"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Button asChild variant="ghost" size="sm">
                          <Link
                            to={buildInventoryWorkspacePath({
                              inventoryId: salesOrder.inventoryId,
                              salesOrderId: salesOrder.id,
                              focus: "sell-orders",
                            })}
                          >
                            Workspace
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ContactDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        mode="create"
        entityLabel="client"
        title="Create client"
        description="Add a new client record to the admin portal."
        submitText="Create client"
        schema={ClientDataSchema}
        variant="client"
        onSubmit={handleCreateClient}
      />

      <ContactDialog
        open={editingClient !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingClient(null);
          }
        }}
        mode="edit"
        entityLabel="client"
        title="Edit client"
        description="Update the selected client details."
        submitText="Save changes"
        schema={ClientDataSchema}
        variant="client"
        contact={editingClient}
        onSubmit={handleUpdateClient}
      />

      <AlertDialog
        open={clientToDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setClientToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete client</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the client record. Existing linked sales
              orders will keep their stored snapshots, but the direct client record
              will be gone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDeleteClient()}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SalesOrderSheet
        soId={selectedSoId}
        open={selectedSoId.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setSearchParams((previous) => {
              const next = new URLSearchParams(previous);
              next.delete("soId");
              return next;
            });
          }
        }}
      />
    </div>
  );
}
